/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import React from "react";
import { useHistory } from "react-router";
import { Badge, badgeClasses, Box, Divider, IconButton, List, ListItem, Paper, styled } from "@mui/material";
import { XIcon } from "../../FontAwesome";
import { translationKeys, useTranslation } from "../../Internationalisierung/i18nBasis";
import AnyUtil, { nameof } from "../../Utils/AnyUtil";
import { useAppSnackbar } from "../AppSnackbarContext";
import AppSnackbarItem, { Level } from "../AppSnackbarContext/AppSnackbarItem";
import AppSnackbarModel from "./AppSnackbarModel";
import AppSnackbarTemplate from "./AppSnackbarTemplate";
import { getLevelColor, getLevelIcon } from "./levelHelpers";

const CLEAR_SUCCESS_ITEMS_DELAY = 2000;

function isSubssetOf<T>(set: T[], subset: T[]) {
    return subset.every(item1 => set.some(item2 => AnyUtil.equals(item1, item2)));
}

const Backdrop = styled("div")(({ theme }) => ({
    zIndex: theme.zIndex.modal + 1,
    position: "fixed",
    inset: 0,
    backgroundColor: "rgb(0 0 0 / 0.5)",

}));

const Snackbar = styled(Paper)(({ theme }) => ({
    zIndex: theme.zIndex.modal + 2,
    position: "fixed",
    left: "50%",
    bottom: "5%",
    width: "min(90%, 1024px)",
    transform: "translateX(-50%)",
    minHeight: "3rem",
    maxHeight: "30vh",
    display: "flex",
    flexDirection: "column",
    color: "inherit",
})) as typeof Paper;

type LevelProps = { level: Level | null };

const SnackbarHeader = styled("header", {
    shouldForwardProp: prop => prop !== nameof<LevelProps>("level")
})<LevelProps>(({ level, theme }) => ({
    display: "flex",
    flexDirection: "row-reverse",
    backgroundColor: getLevelColor(theme, level),
    color: theme.palette.getContrastText(getLevelColor(theme, level)),
}));

const SnackbarBody = styled("div")({
    overflowY: "auto",
});

const MinimizedSnackbar = styled(IconButton, {
    shouldForwardProp: prop => prop !== nameof<LevelProps>("level")
})<LevelProps>(({ level, theme }) => ({
    color: getLevelColor(theme, level),
    position: "fixed",
    inset: `auto ${theme.spacing(2)} 0 auto`,
    zIndex: theme.zIndex.modal + 2,
}));

const LevelBadge = styled(Badge, {
    shouldForwardProp: prop => prop !== nameof<LevelProps>("level")
})<LevelProps>(({ level, theme }) => ({
    [`.${badgeClasses.badge}`]: {
        backgroundColor: getLevelColor(theme, level),
        color: theme.palette.getContrastText(getLevelColor(theme, level)),
    },
}));

export default function AppSnackbar() {
    const [minimized, setMinimized] = React.useState<boolean>(false);
    const { items, clearSuccessItems } = useAppSnackbar();
    const { t } = useTranslation();
    const vm = new AppSnackbarModel(items);
    const hoechstesLevel = vm.getHoechstesLevel();
    const previousItems = React.useRef<AppSnackbarItem[]>([]);
    const templatesProps = vm.getTemplatesProps();
    const LevelIcon = getLevelIcon(hoechstesLevel);
    const history = useHistory();
    const hatAktion = templatesProps.some(t => !!t.action);

    // Erfoglreich-Meldung nach 2 Sekunden aus der Snackbar entfernen
    React.useEffect(() => {
        function clearTimeout(timeout?: number) {
            if (timeout !== undefined) {
                window.clearTimeout(timeout);
            }
        }

        const timeout = window.setTimeout(
            () => {
                clearTimeout(timeout);
                if (items.some(item => item.level === "Success")) {
                    clearSuccessItems();
                }
            },
            CLEAR_SUCCESS_ITEMS_DELAY);

        return clearTimeout.bind(null, timeout);
    }, [items, clearSuccessItems]);

    // Wenn es neue Meldungen gibt, nun eine Aktion gibt oder ein Command erfolgreich ausgeführt wurde
    // Dann die Snackbar öffnen.
    React.useEffect(() => {
        setMinimized(prev => {
            if (hatAktion ||
                items.some(item => item.level === "Success") ||
                !isSubssetOf(previousItems.current, items)) {
                return false;
            }

            return prev;
        });

        previousItems.current = items;
    }, [items, previousItems, hatAktion, setMinimized])

    // Beim Navigieren die vorherigen Items vergessen
    React.useEffect(() => {
        return history.listen(() => {
            previousItems.current = [];
        });
    }, [previousItems, history]);

    function toggleMinimized() {
        setMinimized(prev => !prev);
    }

    if (!vm.hasItems) {
        return null;
    }

    if (minimized) {
        return (
            <MinimizedSnackbar
                level={hoechstesLevel}
                title={t(translationKeys.appSnackbar.oeffnen)}
                onClick={toggleMinimized}
                size="large"
            >
                <LevelBadge level={hoechstesLevel} badgeContent={templatesProps.length}>
                    <LevelIcon />
                </LevelBadge>
            </MinimizedSnackbar>
        );
    }

    return (
        <>
            {vm.showBackdrop && <Backdrop />}
            <Snackbar component="article" elevation={10}>
                <SnackbarHeader level={hoechstesLevel}>
                    {!hatAktion && <IconButton
                        color="inherit"
                        size="small"
                        onClick={toggleMinimized}
                        title={t(translationKeys.appSnackbar.schliessen)}
                    >
                        <XIcon fontSize="inherit" />
                    </IconButton>}
                    {hatAktion && <Box sx={{ height: "20px" }}></Box>}
                </SnackbarHeader>

                <SnackbarBody>
                    <List>
                        {templatesProps.map((props, idx) => (
                            <React.Fragment key={idx}>
                                <ListItem>
                                    <AppSnackbarTemplate {...props} />
                                </ListItem>
                                {idx < templatesProps.length - 1 && <Divider light />}
                            </React.Fragment>
                        ))}
                    </List>
                </SnackbarBody>
            </Snackbar>
        </>
    );
}
